import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SourceDestinationFiltersForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/filters/source-destination-filters-form';
import { eTransportationOptions } from '../../constants/transportation-options.constants';
import { LocalStorageService } from 'src/app/@shared/services/local-storage/local-storage.service';
import { isEqual } from 'lodash'; // Import lodash for deep comparison
import { eSortOptions } from '../../constants/sort-options.constants';


export const initialTransportationsValues = [
  eTransportationOptions.BUS,
  eTransportationOptions.CARMELIT,
  eTransportationOptions.LIGHT_TRAIN,
  eTransportationOptions.PUBLIC_TAXI,
  eTransportationOptions.RAHBALIT,
  eTransportationOptions.TRAIN,
]

export const DEFAULT_FILTERS: SourceDestinationFiltersForm = {
  isAccessible: false,
  transportations: [...initialTransportationsValues],
  sortBy: eSortOptions.FREQUENCY_AND_TRAVEL_TIME, // or whichever default sort value makes sense
  maxTravelTime: 600,
  maxWalkTime: 120,
  changes: true,
};

const STORAGE_KEY = 'filters';

@Injectable({
  providedIn: 'root'
})
export class FilterStateService {
  private filterSubject = new BehaviorSubject<SourceDestinationFiltersForm>(this.loadInitialFilters());

  // Expose the filter as an observable for reactive updates
  currentFilters$ = this.filterSubject.asObservable();

  constructor(private localStorageService: LocalStorageService) {}

  private loadInitialFilters(): SourceDestinationFiltersForm {
      
    const storedFilters = this.localStorageService.getItem(STORAGE_KEY);
    if (storedFilters) {
      try {
        const parsedFilters = JSON.parse(storedFilters as any) as SourceDestinationFiltersForm;

        // Normalize the 'transportations' property: if its length no longer matches
        // the current DEFAULT_FILTERS, force it to the current default transportation options.
        if (!parsedFilters.transportations || parsedFilters.transportations.length === 0) {
          parsedFilters.transportations = DEFAULT_FILTERS.transportations;
        }

        // Merge the stored filters with current defaults.
        const mergedFilters = { ...DEFAULT_FILTERS, ...parsedFilters };
      
        return mergedFilters;
      } catch (error) {
        console.error('Error parsing stored filters:', error);
      }
    }
    return DEFAULT_FILTERS;
  }

  getUserSavedFilters() {
    const storedFilters = this.localStorageService.getItem(STORAGE_KEY);
    if(storedFilters) {
      const parsedFilters = JSON.parse(storedFilters as any) as SourceDestinationFiltersForm;
      return parsedFilters
    }
    return null
  }

  setFilters(filters: SourceDestinationFiltersForm) {
    // If the incoming filters match the default filters, clear storage instead of saving.
    if (this.filtersAreDefault(filters)) {
      this.localStorageService.removeItem(STORAGE_KEY);
      this.clearFilters();
      return;
    }

    const mergedFilters = { ...DEFAULT_FILTERS, ...filters };
    try {

      this.localStorageService.setItem(STORAGE_KEY, JSON.stringify(mergedFilters));

    } catch (error) {
      console.error('Error saving filters:', error);
    }
    this.filterSubject.next(mergedFilters);
  }

  clearFilters() {
    const userFilters = this.getUserSavedFilters()
    this.filterSubject.next(userFilters ? userFilters :DEFAULT_FILTERS);
  }

  getCurrentFilters(): SourceDestinationFiltersForm {
    return this.filterSubject.getValue();
  }
  setFiltersTransient(filters: SourceDestinationFiltersForm) {

    // Only update the in-memory filter state; do not persist filters to localStorage.
    const mergedFilters = { ...DEFAULT_FILTERS, ...filters };
    this.filterSubject.next(mergedFilters);
  }
  filtersAreDefault(filters: SourceDestinationFiltersForm): boolean {
    // Iterate over every key in the default filters.
    return Object.keys(DEFAULT_FILTERS).every(key => {
      const defaultValue = DEFAULT_FILTERS[key];
      const filterValue = filters[key];

      // For array properties, compare sorted arrays.
      if (Array.isArray(defaultValue) && Array.isArray(filterValue)) {
        const sortedDefault = [...defaultValue].sort();
        const sortedFilter = [...filterValue].sort();
        return isEqual(sortedDefault, sortedFilter);
      }

      // For all other types, use deep equality.
      return isEqual(defaultValue, filterValue);
    });
  }
}
